import { FormEvent, useEffect, useMemo, useState } from "react";
import { Cpu, Monitor } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, getFieldErrors } from "../../utils/apiError";
import { Manufacturer, Product, ProductCategory, ProductType, ZatcaPhase } from "../../types";
import { PRODUCT_TYPES, ZATCA_PHASES } from "../../constants/enums";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import { Input, Segmented, Select } from "../../components/ui/FormField";

interface Props {
    open: boolean;
    product: Product | null; // null = create
    onClose: () => void;
    onSaved: () => void;
}

interface FormState {
    name: string;
    type: ProductType | "";
    categoryId: string;
    manufacturerId: string;
    zatcaPhase: ZatcaPhase | "";
}

const EMPTY: FormState = { name: "", type: "", categoryId: "", manufacturerId: "", zatcaPhase: "" };

export default function ProductFormModal({ open, product, onClose, onSaved }: Props) {
    const toast = useToast();
    const { data: categories } = useApi<ProductCategory[]>(open ? "/product-category" : null);
    const { data: manufacturers } = useApi<Manufacturer[]>(open ? "/manufacturer" : null);

    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(
            product
                ? {
                      name: product.name,
                      type: product.type,
                      categoryId: String(product.categoryId),
                      manufacturerId: product.manufacturerId ? String(product.manufacturerId) : "",
                      zatcaPhase: product.zatcaPhase ?? "",
                  }
                : EMPTY
        );
    }, [open, product]);

    // Only categories registered for the selected type
    const categoryOptions = useMemo(
        () =>
            (categories ?? [])
                .filter((c) => c.type === form.type)
                .map((c) => ({ value: String(c.id), label: c.name })),
        [categories, form.type]
    );

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: "" }));
    };

    // Switching type clears the category and the field that no longer applies
    const changeType = (type: ProductType) => {
        if (type === form.type) return;
        setForm((f) => ({ ...f, type, categoryId: "", manufacturerId: "", zatcaPhase: "" }));
        setErrors({});
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!form.type) {
            setErrors({ type: "Choose hardware or software" });
            return;
        }

        const body: Record<string, unknown> = {
            name: form.name.trim(),
            type: form.type,
            categoryId: Number(form.categoryId),
        };

        if (form.type === "HARDWARE") body.manufacturerId = form.manufacturerId ? Number(form.manufacturerId) : null;
        if (form.type === "SOFTWARE") body.zatcaPhase = form.zatcaPhase || null;

        setSaving(true);
        try {
            if (product) {
                await api.put(`/product/${product.id}`, body);
                toast.success("Product updated");
            } else {
                await api.post("/product", body);
                toast.success("Product created");
            }
            onSaved();
        } catch (err) {
            setErrors(getFieldErrors(err));
            toast.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const inUse = (product?._count?.customerProducts ?? 0) > 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            locked={saving}
            title={product ? "Edit product" : "Add product"}
            subtitle="Category, manufacturer and ZATCA phase depend on the product type."
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" form="product-form" loading={saving}>
                        {product ? "Save changes" : "Create product"}
                    </Button>
                </>
            }
        >
            <form id="product-form" onSubmit={handleSubmit} className="space-y-5">
                <Segmented
                    label="Type"
                    required
                    value={form.type}
                    options={PRODUCT_TYPES}
                    onChange={changeType}
                    error={errors.type}
                    disabled={!!product && inUse}
                />
                {product && inUse && (
                    <p className="-mt-3 text-xs text-slate-500 ml-1">
                        This product is assigned to customers, so its type can't be changed.
                    </p>
                )}

                <Input
                    label="Product name"
                    required
                    autoFocus
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder={form.type === "SOFTWARE" ? "e.g. Reztra POS" : "e.g. Sunmi V2s"}
                    error={errors.name}
                />

                {form.type ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            label="Category"
                            required
                            value={form.categoryId}
                            onChange={(e) => set("categoryId", e.target.value)}
                            options={categoryOptions}
                            placeholder={categoryOptions.length ? "Select category" : `No ${form.type.toLowerCase()} categories yet`}
                            error={errors.categoryId}
                            hint={`Only ${form.type.toLowerCase()} categories are listed`}
                        />

                        {form.type === "HARDWARE" ? (
                            <Select
                                label="Manufacturer"
                                value={form.manufacturerId}
                                onChange={(e) => set("manufacturerId", e.target.value)}
                                options={(manufacturers ?? []).map((m) => ({ value: String(m.id), label: m.name }))}
                                placeholder="None"
                                error={errors.manufacturerId}
                            />
                        ) : (
                            <Select
                                label="ZATCA phase"
                                value={form.zatcaPhase}
                                onChange={(e) => set("zatcaPhase", e.target.value as ZatcaPhase | "")}
                                options={ZATCA_PHASES}
                                placeholder="Not applicable"
                                error={errors.zatcaPhase}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-4 p-4 rounded-2xl border border-dashed border-slate-800 text-slate-500 text-sm">
                        <div className="flex gap-2">
                            <Cpu size={18} />
                            <Monitor size={18} />
                        </div>
                        Pick a type to choose the category.
                    </div>
                )}
            </form>
        </Modal>
    );
}